import { useState } from 'react';
import { useInviteMutation } from '../../hooks/useTeam';
import { X } from 'lucide-react';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function InviteModal({ isOpen, onClose }: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [error, setError] = useState('');

  const invite = useInviteMutation();

  const handleClose = () => {
    setEmail('');
    setName('');
    setRole('member');
    setError('');
    invite.reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) { setError('Please fill in all fields.'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setError('Please enter a valid email address.'); return; }
    setError('');
    try {
      await invite.mutateAsync({ email, name, role });
      handleClose();
    } catch (err: any) {
      const code = err?.response?.data?.error?.code ?? err?.code;
      if (code === 'EMAIL_IN_OTHER_ORG') {
        setError('This email is already associated with another organization.');
      } else if (code === 'ALREADY_MEMBER') {
        setError('This person is already a member of your organization.');
      } else {
        setError(err?.message ?? 'Failed to send invitation. Please try again.');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="im-overlay">
      <div className="im-modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 className="im-title">Invite Team Member</h3>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)' }}>
            <X size={18} />
          </button>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="label">Full Name</label>
            <input
              className="input-field"
              type="text"
              placeholder="Jane Smith"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={invite.isPending}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="label">Email Address</label>
            <input
              className="input-field"
              type="email"
              placeholder="jane@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={invite.isPending}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 24 }}>
            <label className="label">Role</label>
            <select
              className="input-field"
              value={role}
              onChange={e => setRole(e.target.value as 'admin' | 'member')}
              disabled={invite.isPending}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleClose} disabled={invite.isPending}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={invite.isPending}>
              {invite.isPending ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
      <style>{`
        .im-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 16px; }
        .im-modal { background: white; border-radius: 12px; box-shadow: 0 20px 60px rgba(0,0,0,0.18); width: 100%; max-width: 420px; padding: 24px; }
        .im-title { font-size: 16px; font-weight: 600; color: var(--navy); margin: 0; }
      `}</style>
    </div>
  );
}
